/**
 * The info icon beside a special-charges label or amount, with a tooltip saying the item, order
 * or quote contains special charges and to contact the sales rep for details. Rendered in the
 * order summary (`components/Shared/PriceBreakdownBox/`), the line-item tables listed in
 * `components/Shared/SpecialCharges/Styles.tsx`, the order, quote, Easy Quote and order
 * confirmation summaries, and `components/Shared/OpenOrderContainer/OpenOrderContainer.tsx`.
 */
import React, { useMemo} from "react";
import { Tooltip } from "@ryerson/frontend.modal";
import { useApplication } from "@ryerson/frontend.application";
import { IconList } from "@ryerson/frontend.assets";
import { SerializedStyles } from "@emotion/react";
import { withLanguage } from "./Content";

import { TooltipContainer, TooltipContent } from "./Styles";

interface SpecialChargesIconProps {
    iconColor: string;
    iconName?: keyof IconList;
    /**
     * Gives the tooltip a 200px minimum width and moves an `orderItem` icon 12px down instead of
     * 9px.
     */
    isMobile?: boolean;
    /** Which tooltip text: an item's charges, the order's, or the quote's. */
    type: "orderItem" | "order" | "quote";
    /** Extra styles on the icon's container. */
    styles?: SerializedStyles;
}

/**
 * Special-charges tooltip icon. Reads the text for the current language from `Content.tsx`
 * without an English fallback.
 */
const SpecialChargesIcon: React.FC<SpecialChargesIconProps> = ({
    type, iconColor, isMobile = false, iconName = 'info-colorable' as keyof IconList, styles
}) => {
    const { localization: { language } } = useApplication();
    const content = withLanguage[language];

    const tooltipText = useMemo(() => {
        if (type === "orderItem") {
            return content.orderItemSpecialChargesTooltipText;
        } if(type === 'quote') { 
            return content.quoteSpecialChargesTooltipText;
        }else {
            return content.orderSpecialChargesTooltipText;
        }
    }, [type, content]);

    const topOffset = type === "orderItem" ? (isMobile ? "12px" : "9px") : "1px";

    return (
        <TooltipContainer iconColor={iconColor} top={topOffset} css={styles}>
            <Tooltip icon={iconName} size="sm">
                <TooltipContent isMobile={isMobile}>{tooltipText}</TooltipContent>
            </Tooltip>
        </TooltipContainer>
    );
};

export default SpecialChargesIcon;